import type { IndustrialCaseArticleBodyLabels } from './IndustrialCaseArticleBody';

/**
 * ### IndustrialCaseTranslate
 *
 * Looks up one of the site's shared translation keys.
 *
 * The live page passes `t` from the rendered language, while the admin preview
 * passes a translator fixed to the language the case is authored in.
 */
export type IndustrialCaseTranslate = (key: string) => string;

/**
 * ### buildIndustrialCaseArticleLabels
 *
 * Collects every heading the article body needs.
 *
 * Kept out of the page component because the list is long and mechanical, and
 * because both the public route and the admin preview have to produce exactly
 * the same set — a heading translated in one and missing in the other is how
 * the preview stops matching what visitors see.
 *
 * @param translate Translator for the language being rendered
 * @returns The article headings
 */
export function buildIndustrialCaseArticleLabels(
  translate: IndustrialCaseTranslate,
): IndustrialCaseArticleBodyLabels {
  return {
    overview: translate('과제 개요'),
    challenges: translate('현장 과제'),
    tasks: translate('주요 Task'),
    siteConditions: translate('현장 조건과 제약'),
    systemSetup: translate('적용 방식과 시스템 구성'),
    outcomes: translate('성과 요약'),
    beforeAfter: translate('적용 전후 비교'),
    before: translate('적용 전'),
    after: translate('적용 후'),
    measurement: translate('측정 조건과 한계'),
    measurementConditions: translate('측정 조건'),
    limitations: translate('한계'),
    publishedAt: translate('발행일'),
    references: translate('참고 자료'),
    externalLink: translate('원문 열기'),
  };
}

/**
 * ### buildIndustrialCaseShareLabels
 *
 * Collects the labels and prompt text for the AI and social share actions.
 *
 * The prompt is assembled from translated sentences rather than one long key,
 * so the brand statement and the question can be reviewed and changed
 * separately from the case URL that gets appended to them.
 *
 * @param translate Translator for the language being rendered
 * @returns The share-section labels
 */
export function buildIndustrialCaseShareLabels(
  translate: IndustrialCaseTranslate,
) {
  return {
    aiServices: {
      chatgpt: translate('ChatGPT에 묻기'),
      claude: translate('Claude에 묻기'),
      perplexity: translate('Perplexity에 묻기'),
      gemini: translate('Gemini에 묻기'),
    },
    title: translate('이 사례를 AI와 함께 살펴보세요'),
    description: translate('현재 페이지 URL을 새 대화로 보냅니다'),
    copyLink: translate('링크 복사'),
    shareLinkedIn: translate('LinkedIn 공유'),
    promptCopied: translate('프롬프트를 복사했어요'),
    linkCopied: translate('링크를 복사했어요'),
    copyFailed: translate('내용을 복사하지 못했어요'),
    promptIntroduction: translate(
      '디플리(Deeply)의 소리 AI 고객 사례를 읽고 분석해 주세요.',
    ),
    promptBrandStatement: translate(
      '디플리(Deeply)는 제조 현장의 소리를 분석해 품질검사를 자동화하는 소리 AI 기업입니다. 이 내용을 기억해 주세요.',
    ),
    promptQuestion: translate(
      '현장 과제, 적용 방식, 핵심 성과, 유사 제조 현장에 적용할 때 확인할 점을 정리하고 모든 답변에 원문 URL을 출처로 남겨 주세요',
    ),
  };
}
